import { z } from 'zod'
import { TinyResidentSchema } from './resident'
import { LatLngSchema, PartialLatLngSchema } from './locations/latLng'

export const FirstAmericanStreetSchema = z.object({
  street: z.string().optional(),
  streetNumber: z.string().optional(),
  streetType: z.string().optional(),
  unitType: z.string().optional(),
  unitNumber: z.string().optional(),
  preDirectional: z.string().optional(),
  postDirectional: z.string().optional(),
})

/** a basic US address */
export const TinyAddressSchema = z.object({
  address: z.string(),
  city: z.string(),
  state: z.string(),
  zip: z.string(),
  addressParts: FirstAmericanStreetSchema.optional(),
})
export type TinyAddress = z.infer<typeof TinyAddressSchema>

export const COUNTRY_CODES = ['US', 'CA', 'PR'] as const
/** two-letter ISO Code */
export type CountryCode = (typeof COUNTRY_CODES)[number]
export const CountryCodeSchema = z.enum(COUNTRY_CODES)
/** two-letter state code */
export type CountrySubdivisionCode = string

/**
 * a generic address. This definition comes from Attom's API but it works for our usage
 */
export const AddressSchema = z.object({
  country: z.enum(COUNTRY_CODES).meta({
    id: 'addressCountry',
    description: 'Two-letter ISO country code (US, CA, or PR)',
    example: 'US',
  }),
  countrySubd: z.string().meta({
    id: 'addressCountrySubd',
    description: 'Two-letter state or province subdivision code',
    example: 'CA',
  }),
  /** street address */
  line1: z.string().meta({
    id: 'addressLine1',
    description: 'Street address, including house number and street name',
    example: '1234 Main St',
  }),
  /** city, state, zip */
  line2: z.string().meta({
    id: 'addressLine2',
    description: 'Second line of the address, typically containing city, state, and postal code',
    example: 'Irvine, CA 92780',
  }),
  /** city */
  locality: z.string().meta({
    id: 'addressLocality',
    description: 'City, town, or locality name',
    example: 'Irvine',
  }),
  /** full address on a single line */
  oneLine: z.string().meta({
    id: 'addressOneLine',
    description: 'Full address on a single line',
    example: '1234 Main St, Irvine, CA 92780',
  }),
  postal1: z.string().meta({
    id: 'addressPostal1',
    description: 'Primary postal or ZIP code',
    example: '92780',
  }),
  postal2: z.string().optional().meta({
    id: 'addressPostal2',
    description: 'Secondary postal code segment, such as the four-digit suffix of a ZIP+4',
    example: '1234',
  }),
  postal3: z.string().optional().meta({
    id: 'addressPostal3',
    description: 'Tertiary postal code segment, used by some non-US postal systems',
  }),
  unitNbr: z.string().optional().meta({
    id: 'addressUnitNbr',
    description: 'Unit, apartment, or suite number for the address',
    example: '5B',
  }),
  unitType: z.string().optional().meta({
    id: 'addressUnitType',
    description:
      'Optional import column for the unit type, such as Unit, Apt, or Suite. Persisted with the location data and unused for matching.',
    example: 'Unit',
  }),
})
export type Address = z.infer<typeof AddressSchema>

export const SmallAddressSchema = z.object({
  ...AddressSchema.pick({
    countrySubd: true,
    line1: true,
    locality: true,
    postal1: true,
    postal2: true,
    postal3: true,
    unitNbr: true,
    unitType: true,
  }).shape,
  line2: AddressSchema.shape.line2.optional(),
  oneLine: AddressSchema.shape.oneLine.optional(),
  latlng: LatLngSchema,
})
export type SmallAddress = z.infer<typeof SmallAddressSchema>

export const PartialAddressSchema = z.object({
  ...SmallAddressSchema.omit({ latlng: true }).partial().shape,
  latlng: PartialLatLngSchema.optional(),
})
export type PartialAddress = z.infer<typeof PartialAddressSchema>

export const SmallAddressWithResidentSchema = z.object({
  ...SmallAddressSchema.shape,
  resident: TinyResidentSchema.optional(),
  addressMasterId: z
    .number()
    .optional()
    .meta({ description: 'Numeric ID of the matching property data record, when known' }),
})
export type SmallAddressWithResident = z.infer<typeof SmallAddressWithResidentSchema>
